import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import * as helpers from '@turf/helpers';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';
import { GeocodingFeature } from '../../../../common/types/mapbox-search-api-v1.types.ts';
import {
  activeItemChanged,
  selectActiveItem,
} from '../../../../pages/item-details/store';
import { compilePath } from '../../../../common/utils/helpers/routes';
import { selectStyle } from '../../../store';
import { useGlMap } from '../../../contexts/context';
import {
  ITEM_TYPES,
  MIN_MAP_POI_ZOOM_LEVEL,
} from '../../../../common/utils/constants/constants';
import { getItemType } from '../../../../common/utils/helpers/item';
import InteractiveMapLayer from '../interactive-map-layer/InteractiveMapLayer';
import { getMapWaterUnionPolygon } from '../../../utils/helpers/mapWaterLayerUtils';

import {
  getDefaultLayerStyleData,
  getCircleLayerStyleData,
  sourceId,
  sourceLayer,
  tileset,
} from './POILayerStyle';
import { PoiTilesetFeature } from '../../../../common/types/poi.types.ts';

export const hidePOIsOnWater = (map, layerId) => {
  if (!map.getLayer('water')) {
    return;
  }

  if (!map.getLayer(layerId)) {
    return;
  }
  const visiblePOIs = map.queryRenderedFeatures(null, {
    layers: [layerId],
  });
  if (!visiblePOIs.length) {
    return;
  }

  const waterPolygon = getMapWaterUnionPolygon(map);
  // check wheter there is water polygon (water layer) or visible polygons
  if (!waterPolygon || !waterPolygon.geometry.coordinates.length) {
    return;
  }

  for (const poi of visiblePOIs) {
    if (poi.state.hidden) {
      continue;
    }

    const point = helpers.point(poi.geometry.coordinates);

    if (booleanPointInPolygon(point, waterPolygon)) {
      map.setFeatureState(
        {
          source: sourceId,
          sourceLayer,
          id: poi.id,
        },
        { selected: false, hover: false, hidden: true },
      );
    }
  }
};

const getItemFromFeature = (feature: PoiTilesetFeature): GeocodingFeature => {
  const { id, geometry, properties } = feature;
  const cityLabels = (properties['addr:city'] || '').split(';');
  const city = cityLabels.length && cityLabels[0];
  const nameLabels = (properties.name || '').split(';');
  const name = nameLabels.length ? nameLabels[0] : '';
  const postcodeLabels = (properties['addr:postcode'] || '').split(';');
  const postcode = postcodeLabels.length && postcodeLabels[0];

  const description = [
    properties['addr:full'],
    [postcode, city].filter(Boolean).join(' '),
    (properties['addr:country'] || '').split(';').at(-1), // example of prop 'addr:country': "States;America;United States of America;US;USA"
  ]
    .filter(Boolean)
    .join(', ');

  let categories: string[] = [];

  if (properties['mapbox:search:categories']) {
    categories = properties['mapbox:search:categories'].split(';');
  }

  return {
    type: 'Feature',
    geometry,
    properties: {
      sourceLayerFeatureId: id,
      description,
      feature_name: name,
      maki: properties.maki,
      mapbox_id: properties.mapbox_id,
      internal_id: undefined,
      matching_name: properties.name,
      metadata: {
        iso_3166_1: properties.iso_3166_1,
        iso_3166_2: properties.iso_3166_2,
        phone: properties.phone,
        website: properties.website,
      },
      place_name: [name, description].filter(Boolean).join(', '),
      place_type: [ITEM_TYPES.poi],
      poi_category: categories,
    },
  };
};

const POILayer = () => {
  const map = useGlMap();
  const history = useHistory();
  const dispatch = useDispatch();
  const currentMapStyle = useSelector(selectStyle);
  const { style: layerStyle, layerId: defaultLayerId } = useMemo(
    () => getDefaultLayerStyleData(currentMapStyle.id),
    [currentMapStyle],
  );

  const { style: circleLayerStyle, layerId: circleLayerId } = useMemo(
    () => getCircleLayerStyleData(currentMapStyle.id),
    [currentMapStyle],
  );

  const item = useSelector(selectActiveItem);
  const selectedFeature = useMemo(
    () => (getItemType(item) === ITEM_TYPES.poi ? item : null),
    [item],
  );
  const [selectedFeatureId, setSelectedFeatureId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    if (!selectedFeature) {
      setSelectedFeatureId(null);

      return;
    }

    if (selectedFeature.properties.sourceLayerFeatureId) {
      setSelectedFeatureId(selectedFeature.properties.sourceLayerFeatureId);

      return;
    }

    // In case feature coming not from the map, but from Search API.
    const findSelectedFeatureOnRender = () => {
      const mapboxId = selectedFeature.properties.mapbox_id;

      if (!map.getLayer(defaultLayerId)) {
        setSelectedFeatureId(null);
        return;
      }

      const features = map.querySourceFeatures(sourceId as string, {
        sourceLayer: sourceLayer as string,
        filter: ['==', ['get', 'mapbox_id'], mapboxId],
      });
      const featureId = features?.[0]?.id || null;

      setSelectedFeatureId(featureId);

      if (featureId) {
        map.off('render', findSelectedFeatureOnRender);
      }
    };

    map.on('render', findSelectedFeatureOnRender);
    return () => {
      map.off('render', findSelectedFeatureOnRender);
    };
  }, [map, selectedFeature, defaultLayerId]);

  const onFeatureClick = useCallback(
    (mapFeature: PoiTilesetFeature) => {
      dispatch(
        activeItemChanged({
          item: getItemFromFeature(mapFeature),
          datasource: tileset.startsWith('mapbox://')
            ? tileset.split('//')[1]
            : tileset,
        }),
      );

      history.push({
        pathname: compilePath('details'),
        search: history.location.search,
      });
    },
    [dispatch, history],
  );

  useEffect(() => {
    if (!map) {
      return;
    }

    const hideCb = () => {
      if (!map || !map.loaded() || map.getZoom() < MIN_MAP_POI_ZOOM_LEVEL) {
        return;
      }

      hidePOIsOnWater(map, defaultLayerId);
    };

    map.on('render', hideCb);

    return () => {
      map.off('render', hideCb);
    };
  }, [map, defaultLayerId]);

  return (
    <>
      <InteractiveMapLayer
        sourceId={sourceId}
        sourceLayer={sourceLayer}
        layerId={defaultLayerId}
        layerStyle={layerStyle}
        selectedFeatureId={selectedFeatureId}
        onFeatureClick={onFeatureClick}
      />
      <InteractiveMapLayer
        sourceId={sourceId}
        sourceLayer={sourceLayer}
        layerId={circleLayerId}
        layerStyle={circleLayerStyle}
        selectedFeatureId={selectedFeatureId}
        onFeatureClick={onFeatureClick}
      />
    </>
  );
};

export default POILayer;
